import { ethers } from "ethers";
import {
  importLegacyRecoveryWallet,
  masterRecoveryWalletFromPhrase,
  mergeMasterRecoveryBundles,
  resolveMasterRecoveryNetwork,
  type MasterRecoveryBundle,
  type MasterRecoveryNetwork,
} from "./masterRecovery";
import {
  createMasterRecoverySession,
  getMasterRecoverySession,
  getMasterRecoverySessionForLink,
  hasStoredMasterRecoverySession,
  hasAnyStoredMasterRecoverySession,
  hasStoredMasterRecoverySessionForPublicWallet,
  linkMasterRecoverySession,
  loadMasterRecoverySession,
  loadSamePublicWalletMasterRecoverySession,
  persistMasterRecoverySession,
  restoreMasterRecoverySession,
  type MasterRecoverySession,
} from "./masterRecoveryStorage";
import type {
  RecoveryPhraseScope,
  RecoveryWalletMaterial,
} from "./recoveryPhrase";

export interface MasterRecoveryMetadata {
  version: 1;
  session: MasterRecoverySession;
  network: MasterRecoveryNetwork;
  /** Additional exact keys whose notes must also be scanned and spendable. */
  legacySpendingKeys: Uint8Array[];
  /** Cleared only by verified publication of imported keys AND legacy notes. */
  legacyBackfillRequired: boolean;
}

export type MasterRecoveryBundleLoader = (
  rootKey: Uint8Array,
  options: { isCurrent: () => boolean },
) => Promise<MasterRecoveryBundle>;

/** Shared policy after the hook has locally verified the signing account. */
export async function connectMasterRecoveryWallet(params: {
  target: RecoveryPhraseScope;
  signature: Uint8Array;
  network: MasterRecoveryNetwork;
  phrase?: string;
  link?: boolean;
  /** Explicitly create a different root for a different public wallet. */
  createSeparate?: boolean;
  legacyMaterial?: RecoveryWalletMaterial;
  isCurrent: () => boolean;
  loadBundle?: MasterRecoveryBundleLoader;
  confirmReplacement?: () => boolean | Promise<boolean>;
}): Promise<{
  material: RecoveryWalletMaterial;
  masterRecovery: MasterRecoveryMetadata;
}> {
  const { target, signature, network, isCurrent } = params;
  const check = () => {
    if (!isCurrent())
      throw new Error(
        "Wallet connection changed during recovery. Reconnect and retry.",
      );
  };
  check();
  if (params.createSeparate) {
    if (params.phrase !== undefined || params.link || params.legacyMaterial)
      throw new Error(
        "Choose either a new private wallet, a link, or a recovery phrase.",
      );
    if (hasStoredMasterRecoverySessionForPublicWallet(target))
      throw new Error(
        "This public wallet already has a saved StealthOS wallet. Unlock or restore it instead of replacing its private keys.",
      );
  }
  let session: MasterRecoverySession;
  if (params.phrase !== undefined) {
    const restored = masterRecoveryWalletFromPhrase(params.phrase);
    let replacement = false;
    if (hasStoredMasterRecoverySession(target)) {
      try {
        const saved = await loadMasterRecoverySession(target, signature, {
          isCurrent,
        });
        replacement =
          !!saved &&
          ethers.hexlify(saved.wallet.rootKey) !==
            ethers.hexlify(restored.rootKey);
      } catch {
        replacement = true;
      }
      check();
      if (replacement && !(await params.confirmReplacement?.()))
        throw new Error(
          "Master-wallet replacement cancelled. Existing encrypted wallet retained.",
        );
    }
    const bundle = params.loadBundle
      ? await params.loadBundle(restored.rootKey, { isCurrent })
      : { version: 1 as const, revision: 0, entries: [] };
    check();
    session = await restoreMasterRecoverySession(
      target,
      signature,
      params.phrase,
      bundle,
      { isCurrent, allowIdentityReplacement: replacement },
    );
  } else if (hasStoredMasterRecoverySession(target)) {
    session = (await loadMasterRecoverySession(target, signature, {
      isCurrent,
    }))!;
  } else if (params.createSeparate) {
    // The user explicitly chose an independent private wallet. Its record
    // belongs only to this new public account; all other local encrypted
    // wallets remain saved under their original scoped keys.
    session = await createMasterRecoverySession(target, signature, {
      isCurrent,
    });
  } else {
    const available =
      (params.link
        ? getMasterRecoverySessionForLink()
        : getMasterRecoverySession(target)) ??
      (await loadSamePublicWalletMasterRecoverySession(target, signature, {
        isCurrent,
      }));
    check();
    let bundle = available?.bundle ?? {
      version: 1 as const,
      revision: 0,
      entries: [],
    };
    if (params.legacyMaterial) {
      if (!params.link)
        throw new Error(
          "Link the existing chain wallet explicitly before importing its keys.",
        );
      bundle = importLegacyRecoveryWallet(
        bundle,
        network,
        params.legacyMaterial,
        { makePrimary: true },
      );
    }
    if (available) {
      available.bundle = bundle;
      session = params.link
        ? await linkMasterRecoverySession(available, target, signature, {
            isCurrent,
          })
        : await persistMasterRecoverySession(available, target, signature, {
            isCurrent,
          });
    } else {
      if (
        getMasterRecoverySessionForLink() ||
        hasAnyStoredMasterRecoverySession()
      )
        throw new Error(
          "A StealthOS wallet is already saved on this device. Unlock it with its original public wallet and choose to link it, or restore the same recovery phrase to use this public wallet.",
        );
      session = await createMasterRecoverySession(
        target,
        signature,
        { isCurrent },
        bundle,
      );
    }
  }
  check();
  if (params.loadBundle && params.phrase === undefined) {
    const found = await params.loadBundle(session.wallet.rootKey, {
      isCurrent,
    });
    check();
    session.bundle = mergeMasterRecoveryBundles([session.bundle, found]);
    session = await persistMasterRecoverySession(session, target, signature, {
      isCurrent,
    });
  }
  const keys = await resolveMasterRecoveryNetwork(
    session.wallet.rootKey,
    network,
    session.bundle,
  );
  check();
  return {
    material: { spendingKey: keys.primary },
    masterRecovery: {
      version: 1,
      session,
      network,
      legacySpendingKeys: keys.legacy,
      legacyBackfillRequired: session.bundle.entries.length > 0,
    },
  };
}
